import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { pathParameter, TagEvent, TagPusherService } from '@libs/shared-services';

import {
  ConfirmDialogComponent,
  ConfirmDialogResult,
  DropdownItem,
  InfiniteScrollListDirective,
  InfiniteScrollListItemDirective,
  TooltipModule,
} from '@libs/ui-lib';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, map, Observable, skip, tap } from 'rxjs';
import { DataState, routes } from '@libs/utils';
import { OnboardingModalService, OnboardingTab } from '@libs/onboarding';
import { AccessType, MessageContentAttachmentMediaTypeEnum } from '@ffd/api-client';
import { AnimationOptions } from 'ngx-lottie';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { selectCurrentUserProfile } from '@libs/store';
import { CollabSidebarComponent } from '../collab-sidebar';
import {
  CollaborationEditorDialogComponent,
  CollaborationEditorDialogData,
  CollaborationEditorDialogResult,
} from '../collaboration-editor-dialog';
import { MessageComponent } from '../message';
import { MessageInputComponent, MessageInputVM } from '../message-input';
import { DateEqualsPipe } from '../../pipes';
import { DatesHaveBiggerGapPipe } from '../../pipes/dates-have-bigger-gap';
import { NewMessageMarkerComponent } from '../new-message-marker';
import { UnsendMessageConfirmDialogComponent } from '../unsend-message-confirm-dialog';
import { CollaborationChatHeaderComponent } from '../collaboration-chat-header';
import { SupportCollabChatHeaderComponent } from '../support-collab-chat-header';
import { CollaborationDynamicTitlePipe } from '../../pipes/collaboration-dynamic-title';
import { DownloadAttachmentsService } from '../../services';
import { CollabSidebarService } from '../collab-sidebar/collab-sidebar.service';
import { MessageInputService } from '../message-input/message-input.service';
import { CollaborationScreenService } from './collaboration-screen.service';

@Component({
  selector: 'lib-collab-screen',
  imports: [
    CommonModule,
    CollabSidebarComponent,
    ReactiveFormsModule,
    CollaborationChatHeaderComponent,
    SupportCollabChatHeaderComponent,
    MessageInputComponent,
    MessageComponent,
    InfiniteScrollListDirective,
    InfiniteScrollListItemDirective,
    TranslateModule,
    DateEqualsPipe,
    DatesHaveBiggerGapPipe,
    TooltipModule,
    NewMessageMarkerComponent,
    CollaborationDynamicTitlePipe,
    SvgIconComponent,
  ],
  templateUrl: './collab-screen.component.html',
  styleUrl: './collab-screen.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollabScreenComponent implements OnInit, OnDestroy {
  @ViewChild('scrollContainer') private readonly scrollContainer!: ElementRef<HTMLDivElement>;
  @ViewChild(MessageInputComponent) public messageInputComponent!: MessageInputComponent;

  private readonly route = inject(ActivatedRoute);
  private readonly screenService = inject(CollaborationScreenService);
  private readonly filtersService = inject(CollabSidebarService);
  private readonly messageInputService = inject(MessageInputService);
  private readonly dialog = inject(Dialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly onboardingModalService = inject(OnboardingModalService);
  private readonly tagPusherService = inject(TagPusherService);
  private readonly downloadAttachmentsService = inject(DownloadAttachmentsService);
  private readonly store = inject(Store);

  public readonly sites = computed(() => this.screenService.viewModel().sites);
  public readonly siteOptions = computed<DropdownItem<string>[]>(() =>
    this.sites().map((site) => ({ value: site.id, label: site.name, secondaryLabel: site.postfix })),
  );
  public readonly collabs = computed(() => this.screenService.viewModel().collaborations);
  public readonly currentUserIds = computed(() => this.screenService.viewModel().currentUserIds);
  public readonly searchTerm = computed(() => this.screenService.viewModel().filters.searchTerm);
  public readonly chatHeaderInfo = computed(() => this.screenService.viewModel().chatHeaderInfo);
  public readonly selectedCollab = computed(() => this.screenService.viewModel().selectedCollaboration);
  public readonly selectedSite = computed(() => this.screenService.viewModel().selectedSite);
  public readonly selectedSiteId = computed(() => this.selectedSite()?.id);
  public readonly isShowcase = computed(() => this.selectedSite()?.id === routes.showcaseSiteId);
  public readonly disableImageUpload = computed(() => this.screenService.viewModel().disableImageUpload);

  public readonly messages = computed(() => this.screenService.viewModel().messages);
  public readonly newMessageMarkerBeforeMessageId = computed(() => this.screenService.viewModel().newMessageMarkerBeforeMessageId);
  public readonly recordingStatus = this.screenService.recordingStatus;

  protected readonly patientId = computed(() => this.screenService.viewModel().patientId);
  public readonly showSiteFilter = computed(
    () => !this.patientId() && this.selectedSiteId() !== routes.showcaseSiteId && this.sites().length > 1,
  );
  public readonly filtersOpen = computed(() => this.screenService.viewModel().filters.open);
  public readonly hasPendingMessages = computed(() => this.screenService.hasPendingMessagesSignal());
  protected readonly messageInDownloadMode = computed(() => this.downloadAttachmentsService.viewModel().messageInDownloadMode);
  protected readonly hasImagingSoftware = computed(() => this.screenService.viewModel().hasImagingSoftware);
  protected readonly selectedPatient = computed(() => this.screenService.viewModel().selectedPatient);
  public readonly speechRecognitionStatus = computed(() => this.screenService.speechRecognitionStatus());
  protected readonly supportCollabId = this.store.selectSignal(selectCurrentUserProfile)()?.supportCollaborationId;
  protected readonly isSupportCollab = computed(() => this.selectedCollab().data?.id === this.supportCollabId);
  protected readonly isTyping = signal(false);
  protected readonly supportCollabHeaderExpanded = computed(() => !this.isTyping());

  protected readonly hasDeletedCollabs = computed(() => this.screenService.viewModel().hasDeletedCollabs);
  protected readonly participantCount = computed(() => this.selectedCollab().data?.participants.length || 0);

  // the system messages will be implemented in collab 2, so we need to filter them out for now
  protected readonly messagesWithoutSystemMessages = computed(() => this.messages()?.filter((msg) => !msg.isSystemMessage) || []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private dialogRef: DialogRef<any> | null = null;
  private confirmDialogRef: DialogRef<ConfirmDialogResult> | null = null;
  public DataState = DataState;
  public readonly lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  public ngOnInit(): void {
    const patientId = this.route.snapshot.paramMap.get(pathParameter('patientId'));
    this.tagPusherService.pushTag({ event: TagEvent.CollaborationOpened, patient_id: patientId || undefined });
    this.screenService.init(patientId ?? undefined, true, {
      directOnly: this.filtersService.state().filterDirectOnly,
      unreadOnly: this.filtersService.state().filterUnreadOnly,
      teamCollab: this.filtersService.state().filterTeamCollab,
      clinicalCollab: this.filtersService.state().filterClinicalCollab,
      recentlyDeleted: this.filtersService.state().filterRecentlyDeleted,
      searchTerm: this.filtersService.state().searchTerm,
      selectedSiteIds: this.filtersService.state().filterSiteIds,
    });
    this.downloadAttachmentsService.setPatientId(this.patientId() ?? '');
    this.onboardingModalService.setActiveTab(OnboardingTab.Collaboration);
    this.screenService.activatePage();

    // Listen for site changes (e.g., exiting Showcase)
    this.route.params
      .pipe(
        map((params) => params['siteId']),
        distinctUntilChanged(),
        skip(1), // Skip the initial emission since init() already handled it
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.screenService.updateFilters({
          filters: {
            directOnly: this.filtersService.state().filterDirectOnly,
            unreadOnly: this.filtersService.state().filterUnreadOnly,
            searchTerm: this.filtersService.state().searchTerm,
            selectedSiteIds: this.filtersService.state().filterSiteIds,
          },
          fetchCollabs: true,
          clearCollabs: true,
        });
      });
  }

  public ngOnDestroy(): void {
    this.dialogRef?.close();
    this.screenService.destroy();
  }

  public loadOlderMessage(): void {
    this.screenService.getAndAppendMessages();
  }

  public loadOlderCollabs(): void {
    this.screenService.getAndAppendCollaborations({ selectFirstCollaboration: true, append: true });
  }

  protected onSelectCollab(collabId: string): void {
    if (!this.hasPendingMessages()) {
      this.screenService.selectCollaboration(collabId);
      this.isTyping.set(false);
    }

    if (this.hasPendingMessages()) {
      this.handlePendingMessages().subscribe((result) => {
        if (result?.confirm) {
          this.screenService.selectCollaboration(collabId);
          this.isTyping.set(false);
        }
      });
    }
  }

  protected onSend(message: MessageInputVM): void {
    const hasRecordingAttachment = message.attachments.uncategorized.some(
      (attachment) => attachment.mediaType === MessageContentAttachmentMediaTypeEnum.RECORDING,
    );
    if (hasRecordingAttachment) {
      this.tagPusherService.pushTag({ event: TagEvent.ActiveCaptureSent, patient_id: this.patientId() });
    }

    this.screenService.sendMessage(message);
  }

  protected onRecordClick(): void {
    if (this.recordingStatus() !== 'recording') {
      this.tagPusherService.pushTag({ event: TagEvent.ActiveCaptureStarted, patient_id: this.patientId() });
    }
    this.screenService.toggleRecord();
  }

  protected onMessageTyping(isTyping: boolean): void {
    this.isTyping.set(isTyping);
  }

  protected onMouseWheel(event: WheelEvent): void {
    event.preventDefault();

    this.scrollContainer.nativeElement.scrollTop -= (event as WheelEvent).deltaY;
  }

  protected onToggleFilters(): void {
    this.screenService.toggleFilters();
  }

  protected onClearFilters(): void {
    this.screenService.clearFilters();
  }

  protected onUnsendMessage(collabId: string | undefined, messageId: string): void {
    if (!collabId) {
      throw new Error('CollabId is undefined');
    }

    this.dialogRef = this.dialog.open<boolean>(UnsendMessageConfirmDialogComponent, {
      backdropClass: ['!opacity-0'],
    });
    this.dialogRef.closed.subscribe((confirmed) => {
      if (confirmed) {
        this.screenService.unsendMessage({ collabId, messageId });
      }
    });
  }

  protected onMarkMessageUnread(collabId: string | undefined, messageId: string): void {
    if (!collabId) {
      throw new Error('CollabId is undefined');
    }

    this.screenService.markMessageAsUnread({ collabId, messageId });
  }

  protected onRetrySendMessage(messageId: string): void {
    this.screenService.retrySendMessage(messageId);
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  protected openCreateCollabDialog(isInGlobalCollabMode: boolean): void {
    const siteId = this.selectedSiteId();
    const siteOptions = this.siteOptions();
    const selectedPatient = this.selectedPatient().data;
    const hasMoreFullAccessSites =
      this.sites().filter((site) => site.provider?.accessType === AccessType.FULL_ACCESS && site.id !== routes.showcaseSiteId).length > 1;

    this.tagPusherService.pushTag({ event: TagEvent.CollaborationStarted, patient_id: this.patientId() });

    this.openDialog({
      linkedPatient:
        selectedPatient && siteId
          ? {
              id: selectedPatient.id,
              pmsPatientId: selectedPatient.guarId?.toString() ?? '',
              firstName: selectedPatient.firstName,
              lastName: selectedPatient.lastName,
              participantRole: 'patient',
            }
          : undefined,
      siteId,
      sites: siteOptions,
      showSiteSelector: isInGlobalCollabMode && hasMoreFullAccessSites,
      currentUserIds: this.currentUserIds(),
    }).subscribe((result) => {
      if (result) {
        if (!this.hasPendingMessages()) {
          this.createCollaboration(result);
        }
        if (this.hasPendingMessages()) {
          this.handlePendingMessages().subscribe((unsentResult) => {
            if (unsentResult?.confirm) {
              this.createCollaboration(result);
            }
          });
        }
      }
    });
  }

  private createCollaboration(result: CollaborationEditorDialogResult): void {
    this.screenService.getAndAppendCollaborations({ selectFirstCollaboration: true, append: true });
    this.tagPusherService.pushTag({
      event: TagEvent.CollaborationCreated,
      patient_linked: result.linkedPatient ? 1 : 0,
      patient_invited: result.patient ? 1 : 0,
      participant_added: result.employees.length + (result.patient ? 1 : 0) + result.emailParticipants.length,
      patient_id: this.patientId(),
    });
    this.screenService.createCollaboration(result);
  }

  protected onOpenAddParticipantsModal(): void {
    const isSoftDeletedCollab = !!this.selectedCollab().data?.daysLeftOfDeletion;
    if (isSoftDeletedCollab) {
      return;
    }

    const isCreatedBySelf = this.selectedCollab().data?.isCreatedBySelf;
    const isUserPatient = !!this.selectedSite()?.patient;
    const userId = this.selectedSite()?.provider?.userId;
    const createdByUserId = this.selectedCollab().data?.createdByUserId;
    const isUserSiteAdmin = this.selectedCollab().data?.isUserSiteAdmin;

    this.openDialog({
      siteId: this.chatHeaderInfo()?.siteId,
      sites: this.siteOptions(),
      linkedPatient: this.chatHeaderInfo()?.linkedPatient,
      isEditing: true,
      employeeParticipants: this.chatHeaderInfo()?.employeeParticipants,
      patientParticipant: this.chatHeaderInfo()?.patientParticipant,
      emailParticipants: this.chatHeaderInfo()?.emailParticipants,
      subject: this.chatHeaderInfo()?.subject,
      disableEdit: isUserPatient && !isCreatedBySelf,
      currentUserIds: this.currentUserIds(),
      isCreatedBySelf,
      createdByUserId,
      isUserSiteAdmin,
      userId,
    }).subscribe((result) => {
      if (result) {
        if (!this.hasPendingMessages()) {
          this.screenService.updateCollaboration(result);
        }
        if (this.hasPendingMessages()) {
          this.handlePendingMessages().subscribe((unsentResult) => {
            if (unsentResult?.confirm) {
              this.screenService.updateCollaboration(result);
            }
          });
        }
      }
    });
  }

  protected onDownloadTap(messageId: string): void {
    this.tagPusherService.pushTag({
      event: TagEvent.CollaborationDownloadButtonTap,
      location: 'collaboration_message',
      patient_id: this.patientId(),
    });
    if (!this.messageInDownloadMode()) {
      this.downloadAttachmentsService.setMessageInDownloadMode(messageId);
    } else if (this.messageInDownloadMode() === messageId) {
      this.downloadAttachmentsService.resetMessageInDownloadMode();
    } else {
      this.onCancelSelectionClick(messageId);
    }
  }

  protected onDownloadAttachments(): void {
    this.downloadAttachmentsService.downloadAttachments();
    this.downloadAttachmentsService.resetMessageInDownloadMode();
  }

  // eslint-disable-next-line rxjs/finnish
  private openDialog(data: CollaborationEditorDialogData): Observable<CollaborationEditorDialogResult | undefined> {
    this.dialogRef = this.dialog.open<CollaborationEditorDialogResult, CollaborationEditorDialogData>(CollaborationEditorDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      panelClass: ['w-[787px]'],
      disableClose: true,
      data: data,
    });
    return this.dialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef));
  }

  public onSelection(type: 'single' | 'multiple' | 'all'): void {
    this.tagPusherService.pushTag({
      event: TagEvent.CollaborationImageSelected,
      type,
      location: 'collaboration_message',
      patient_id: this.patientId(),
    });
  }

  public onDeselection(type: 'single' | 'multiple' | 'all'): void {
    this.tagPusherService.pushTag({
      event: TagEvent.CollaborationImageDeselected,
      type,
      location: 'collaboration_message',
      patient_id: this.patientId(),
    });

    /*     if (type === 'all') {
      this.downloadAttachmentsService.resetMessageInDownloadMode();
    } */
  }

  // eslint-disable-next-line rxjs/finnish
  public handlePendingMessages(): Observable<ConfirmDialogResult | undefined> {
    return this.dialog
      .open<ConfirmDialogResult>(ConfirmDialogComponent, {
        backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
        width: '480px',
        data: {
          title: 'collab_unsent_message_alert_title',
          description: 'collab_unsent_message_alert_description',
          cancelLabel: 'collab_unsent_message_alert_cancel',
          confirmLabel: 'collab_unsent_message_alert_confirm',
          destructive: true,
        },
      })
      .closed.pipe(
        tap((result) => {
          if (result?.confirm) {
            this.screenService.resetPendingMessages();
          }
        }),
      );
  }

  protected onCancelSelectionClick(messageId: string): void {
    const dialogData = {
      title: 'collab_message_select_images_cancel_alert_title',
      description: 'collab_message_select_images_cancel_alert_warning',
      cancelLabel: 'collab_message_select_images_cancel',
      confirmLabel: 'collab_message_select_images_cancel_alert_confirm',
      destructive: true,
    };

    this.confirmDialogRef = this.dialog.open<ConfirmDialogResult>(ConfirmDialogComponent, {
      backdropClass: ['backdrop-blur-overlay', 'bg-overlay-light'],
      maxWidth: '460px',
      panelClass: [],
      data: dialogData,
    });

    this.confirmDialogRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result?.confirm) {
        this.downloadAttachmentsService.setAttachments([]);
        this.downloadAttachmentsService.setMessageInDownloadMode(messageId);
      }
    });
  }

  public onToggleDictation(): void {
    this.messageInputService.toggleSpeechRecognitionListening();
  }

  public onSelectDevice(deviceId: string): void {
    this.messageInputService.selectMicrophone(deviceId);
  }
}
